// src/utils/excelParser.js
import * as XLSX from 'xlsx';

export async function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          defval: '',
        });
        const headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];

        resolve({
          rows: jsonData,
          headers,
          rowCount: jsonData.length,
          sheetName: firstSheetName,
        });
      } catch (error) {
        reject(new Error(`Error al leer el archivo Excel: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsArrayBuffer(file);
  });
}

export function generateExcelFile(data, filename = 'entregas.xlsx') {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Entregas');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function downloadExcelTemplate(userProducts = []) {
  const data = [
    [
      'Nombre y Apellido',
      'WhatsApp',
      'Distrito',
      'SKU 1',
      'Cantidad 1',
      'SKU 2',
      'Cantidad 2',
      'SKU 3',
      'Cantidad 3',
      'SKU 4',
      'Cantidad 4',
      'SKU 5',
      'Cantidad 5',
      'Cobrar',
      'Notas',
      'Direccion',
    ],
    [
      'John Doe',
      '912345678',
      'Miraflores',
      'FAJA-001',
      '2',
      'CINT-002',
      '1',
      '',
      '',
      '',
      '',
      '',
      '',
      '89.90',
      'Ejemplo de nota',
      'Av. Ejemplo 123',
    ],
    [
      'Maria Garcia',
      '987654321',
      'San Isidro',
      'CINT-002',
      '1',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '45.00',
      'Llamar antes',
      'Jr. Los Pinos 456',
    ],
  ];

  const instructions = [
    ['INSTRUCCIONES IMPORTANTES - FAST MAX CARGA MASIVA'],
    [''],
    ['La fecha se selecciona en la interfaz web, no en este Excel.'],
    [''],
    ['CAMPOS OBLIGATORIOS:'],
    ['- Nombre y Apellido: nombre completo del destinatario'],
    ['- WhatsApp: exactamente 9 digitos, sin codigo de pais +51'],
    ['- Distrito: debe coincidir con la lista de distritos'],
    ['- Direccion: direccion completa de entrega'],
    ['- SKU 1: codigo del primer producto'],
    [''],
    ['PRODUCTOS:'],
    ['- Use SKU 1 / Cantidad 1 para el primer producto'],
    ['- Use SKU 2-5 / Cantidad 2-5 para productos adicionales en la misma entrega'],
    ['- El campo Producto fue eliminado para evitar errores de tipeo'],
    ['- El sistema usara el nombre real del producto guardado en Fast Max'],
    ['- Consulte la pestana Productos para copiar los SKUs disponibles'],
    [''],
    ['CAMPOS OPCIONALES:'],
    ['- Cobrar: monto a cobrar contraentrega, por defecto 0'],
    ['- Notas: informacion adicional para el courier'],
    [''],
    ['DISTRITOS DISPONIBLES:'],
    ["Copie exactamente el nombre del distrito de la pestana 'Distritos'."],
  ];

  const districtSheetData = [
    ['LISTA DE DISTRITOS VALIDOS - FAST MAX'],
    [''],
    ['Copie el nombre exactamente como aparece aqui:'],
    [''],
    ['ZONA SUR:'],
    ['Chorrillos', 'San Juan de Miraflores', 'Villa El Salvador', 'Villa Maria del Triunfo', 'Barranco', 'Surquillo', 'Lurin', 'Pachacamac'],
    [''],
    ['ZONA CENTRO:'],
    ['San Isidro', 'San Borja', 'Miraflores', 'Santiago de Surco', 'Lince', 'Jesus Maria', 'Magdalena del Mar', 'San Miguel', 'Pueblo Libre', 'Brena', 'Cercado de Lima', 'La Victoria'],
    [''],
    ['ZONA ESTE:'],
    ['Ate', 'Santa Anita', 'El Agustino', 'Lurigancho', 'La Molina', 'San Juan de Lurigancho', 'San Luis', 'Chosica', 'Huaycan'],
    [''],
    ['ZONA NORTE:'],
    ['Comas', 'Independencia', 'San Martin de Porres', 'Los Olivos', 'Rimac', 'Puente Piedra', 'Carabayllo', 'Ancon', 'Santa Rosa'],
    [''],
    ['CALLAO:'],
    ['Callao', 'Bellavista', 'Carmen de la Legua', 'La Perla', 'La Punta', 'Ventanilla'],
  ];

  const fallbackProducts = [
    ['FAJA-001', 'Faja Clasica Negra', '89.90', '10'],
    ['FAJA-002', 'Faja Premium Beige', '129.90', '5'],
    ['CINT-001', 'Cinturon Deportivo M', '45.00', '8'],
    ['CINT-002', 'Cinturon Deportivo L', '45.00', '12'],
  ];

  const productRows = userProducts.length > 0
    ? userProducts.map(product => [
        product.sku,
        product.title,
        product.price,
        product.available_quantity ?? 0,
      ])
    : fallbackProducts;

  const productsSheet = [
    ['PRODUCTOS DISPONIBLES'],
    [''],
    ['SKU', 'Producto', 'Precio', 'Stock Fast Max'],
    ...productRows,
    [''],
    ['CONSEJOS:'],
    ['- Copie el SKU exactamente como aparece aqui'],
    ['- Si una entrega tiene dos productos, use SKU 1 y SKU 2 en la misma fila'],
    ['- La cantidad puede superar el stock; el sistema permitira inventario negativo si corresponde'],
  ];

  const wsData = XLSX.utils.aoa_to_sheet(data);
  const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
  const wsDistricts = XLSX.utils.aoa_to_sheet(districtSheetData);
  const wsProducts = XLSX.utils.aoa_to_sheet(productsSheet);

  wsData['!cols'] = [
    { wch: 20 },
    { wch: 12 },
    { wch: 20 },
    { wch: 14 },
    { wch: 12 },
    { wch: 14 },
    { wch: 12 },
    { wch: 14 },
    { wch: 12 },
    { wch: 14 },
    { wch: 12 },
    { wch: 14 },
    { wch: 12 },
    { wch: 10 },
    { wch: 30 },
    { wch: 35 },
  ];
  wsInstructions['!cols'] = [{ wch: 90 }];
  wsDistricts['!cols'] = [{ wch: 80 }];
  wsProducts['!cols'] = [{ wch: 18 }, { wch: 35 }, { wch: 12 }, { wch: 16 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsData, 'Plantilla');
  XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instrucciones');
  XLSX.utils.book_append_sheet(wb, wsDistricts, 'Distritos');
  XLSX.utils.book_append_sheet(wb, wsProducts, 'Productos');

  XLSX.writeFile(wb, 'Plantilla_Carga_Masiva_FastMax.xlsx');
}
